import { LssProp } from '@lucidhq/lucidium';
import { Field, useFormikContext } from '@lucidhq/lucidium/components/FormV2';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';

import { getPanelCurrencyAmount } from '@/utils/getPanelCurrencyAmount';
import { getValueByPath } from '@/utils/getValueByPath';
import { PanelistCurrency } from '@/utils/types';

export interface FormCurrencyAmountFieldProps {
  name: string;
  testId?: string;
  isDisabled?: boolean;
  min?: number;
  max?: number;
  isRequired?: boolean;
  lss?: LssProp;
  currencyCode?: string | null;
  currencyDisplayDecimals?: number;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
  isPanelCurrency?: boolean;
  panelistCurrencyAmount?: number;
  panelistCurrency?: PanelistCurrency | null;
  groupLss?: LssProp;
}

interface GenericFormValues {
  [key: string]: any;
}

export const FormCurrencyAmountField = ({
  name,
  testId,
  isDisabled,
  min,
  max,
  isRequired,
  lss,
  currencyCode,
  currencyDisplayDecimals,
  onChange,
  onBlur,
  isPanelCurrency,
  panelistCurrencyAmount,
  panelistCurrency,
  groupLss,
}: FormCurrencyAmountFieldProps) => {
  const { setFieldValue, values } = useFormikContext<GenericFormValues>();
  const value =
    isPanelCurrency && panelistCurrencyAmount !== undefined
      ? getPanelCurrencyAmount({
          panelistCurrencyAmount,
          currencyMinorUnits: currencyDisplayDecimals,
          panelistCurrency,
        })
      : getValueByPath<number>(values, name);

  const [displayValue, setDisplayValue] = useState('');
  const displayDecimals = currencyDisplayDecimals ?? 0;

  useEffect(() => {
    if (value !== undefined && value !== parseFloat(displayValue)) {
      setDisplayValue(value.toFixed(displayDecimals));
    }
  }, [value, name, displayValue, displayDecimals]);

  const step = Math.pow(10, -displayDecimals);

  const handleBlur = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(event);
      }
      if (event.target.value) {
        const formattedValue = parseFloat(event.target.value).toFixed(
          displayDecimals
        );
        setDisplayValue(formattedValue);
      }
    },
    [displayDecimals]
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(event);
      } else {
        setFieldValue(name, parseFloat(event.target.value));
      }
      setDisplayValue(event.target.value);
    },
    [name]
  );

  return (
    <Field
      disabled={isDisabled}
      data-testid={testId}
      type="number"
      name={name}
      required={isRequired}
      lss={lss}
      onBlur={handleBlur}
      step={step}
      rightIcon={currencyCode}
      min={min}
      max={max}
      onChange={handleChange}
      value={displayValue}
      groupLss={groupLss}
    />
  );
};
